// Access.tsx — экран «Роли и доступы» (Ф8, owner-only). Веб-админы tenant'а
// (грант/смена роли/отзыв), матрица прав (read-only, из authz) и компании
// (tenant'ы) с переключением/созданием. Сервер проверяет права на каждом
// действии — UI лишь удобная обёртка.

import { useState } from 'react';
import { api, ApiError } from '../api/client';
import type { AdminSummary, TenantSummary, WebRoleContract } from '../api/contract';
import { StateCard } from '../components/StateBlock';
import { useApi } from '../lib/useApi';

const ROLE_LABEL: Record<WebRoleContract, string> = {
  owner: 'Владелец',
  director: 'Директор',
  manager: 'Менеджер',
  observer: 'Наблюдатель',
};

const CAP_LABEL: Record<string, string> = {
  viewDashboard: 'Дашборд и отчёты',
  viewOthers: 'Чужие отчёты',
  analytics: 'Аналитика и тренды',
  editSurveys: 'Редактировать анкеты',
  editSchedule: 'Настраивать расписание',
  manageRespondents: 'Управлять сотрудниками',
  exportXlsx: 'Экспорт XLSX',
  manageAccess: 'Управлять доступами',
  manageTenants: 'Управлять компаниями',
};

const GRANTABLE: WebRoleContract[] = ['owner', 'director', 'manager', 'observer'];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

export default function Access() {
  const [reloadKey, setReloadKey] = useState(0);
  const state = useApi(() => api.access(), [reloadKey]);
  const reload = () => setReloadKey((k) => k + 1);

  if (state.loading) return <StateCard kind="loading" message="Загрузка доступов…" />;
  if (state.error || !state.data) {
    return <StateCard kind="error" message={state.error ?? 'Не удалось загрузить'} />;
  }

  const { admins, tenants, roles, capabilities } = state.data;
  const capKeys = [...new Set(roles.flatMap((r) => capabilities[r] ?? []))].sort(
    (a, b) =>
      Object.keys(CAP_LABEL).indexOf(a) - Object.keys(CAP_LABEL).indexOf(b),
  );

  return (
    <section className="page">
      <div className="ph">
        <h1>Роли и доступы</h1>
      </div>
      <p className="sub">
        Кто заходит в веб-панель и что видит. Разделение прав — основа для второго клиента и для
        разницы «директор / менеджер». Права проверяются на сервере.
      </p>

      <AdminsCard admins={admins} onChanged={reload} />
      <MatrixCard roles={roles} capKeys={capKeys} capabilities={capabilities} />
      <TenantsCard tenants={tenants} onChanged={reload} />
    </section>
  );
}

// ====== Администраторы ======
function AdminsCard({ admins, onChanged }: { admins: AdminSummary[]; onChanged: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h3>👥 Администраторы компании</h3>
      {error && <p style={{ color: 'var(--red)', fontSize: 13 }}>{error}</p>}
      <table className="mgmt">
        <thead>
          <tr>
            <th>Имя</th>
            <th>Роль</th>
            <th>Telegram ID</th>
            <th>Последний вход</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {admins.map((a) => (
            <tr key={a.telegramUserId}>
              <td>
                <b>{a.name ?? '—'}</b> {a.isSelf && <span className="chip ok">вы</span>}
              </td>
              <td>
                <select
                  value={a.role}
                  disabled={busy}
                  onChange={(e) =>
                    void run(() =>
                      api.grantAdmin({
                        telegramUserId: a.telegramUserId,
                        role: e.target.value as WebRoleContract,
                      }),
                    )
                  }
                >
                  {GRANTABLE.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <span className="toks">{a.telegramUserId}</span>
              </td>
              <td>{fmtDate(a.lastLoginAt)}</td>
              <td>
                <button
                  className="iconbtn"
                  title="Отозвать доступ"
                  disabled={busy || a.isSelf}
                  onClick={() => void run(() => api.revokeAdmin(a.telegramUserId))}
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <InviteAdmin onChanged={onChanged} />
    </div>
  );
}

function InviteAdmin({ onChanged }: { onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [tgId, setTgId] = useState('');
  const [role, setRole] = useState<WebRoleContract>('director');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function grant() {
    setBusy(true);
    setError(null);
    try {
      await api.grantAdmin({ telegramUserId: tgId.trim(), role });
      setOpen(false);
      setTgId('');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="addrow" style={{ marginTop: 12 }}>
        <button onClick={() => setOpen(true)}>+ Пригласить администратора</button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div className="field" style={{ marginBottom: 0 }}>
        <label>Telegram ID</label>
        <input
          className="tinput"
          style={{ width: 160 }}
          value={tgId}
          inputMode="numeric"
          placeholder="напр. 123456789"
          onChange={(e) => setTgId(e.target.value.replace(/\D/g, ''))}
        />
      </div>
      <div className="field" style={{ marginBottom: 0 }}>
        <label>Роль</label>
        <select className="tselect" value={role} onChange={(e) => setRole(e.target.value as WebRoleContract)}>
          {GRANTABLE.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL[r]}
            </option>
          ))}
        </select>
      </div>
      <button className="btn" disabled={busy || tgId.trim() === ''} onClick={grant}>
        {busy ? 'Добавляю…' : 'Выдать доступ'}
      </button>
      <button className="btn ghost" onClick={() => setOpen(false)}>
        Отмена
      </button>
      {error && <p style={{ color: 'var(--red)', fontSize: 13, width: '100%' }}>{error}</p>}
      <p style={{ color: 'var(--muted)', fontSize: 12, width: '100%', margin: 0 }}>
        Доступ выдаётся по Telegram ID. Человек заходит magic-link'ом из бота (нужна привязка к
        боту). Наблюдатель-нереспондент без привязки — пока не поддержан.
      </p>
    </div>
  );
}

// ====== Матрица прав ======
function MatrixCard({
  roles,
  capKeys,
  capabilities,
}: {
  roles: WebRoleContract[];
  capKeys: string[];
  capabilities: Record<string, string[]>;
}) {
  return (
    <div className="card" style={{ marginTop: 18 }}>
      <h3>
        🔑 Матрица прав <span className="tag">что может каждая роль</span>
      </h3>
      <table className="matrix">
        <thead>
          <tr>
            <th>Возможность</th>
            {roles.map((r) => (
              <th key={r}>
                <div className="rolehead">{ROLE_LABEL[r]}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {capKeys.map((cap) => (
            <tr key={cap}>
              <td>{CAP_LABEL[cap] ?? cap}</td>
              {roles.map((r) => {
                const has = (capabilities[r] ?? []).includes(cap);
                return (
                  <td key={r} className={has ? 'yes' : 'no'}>
                    {has ? '✓' : '✕'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ====== Компании (tenant) ======
function TenantsCard({ tenants, onChanged }: { tenants: TenantSummary[]; onChanged: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');

  async function run(fn: () => Promise<unknown>, reloadPage = false) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (reloadPage) window.location.assign('/');
      else onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось');
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <h3>🏢 Компании (супер-админ)</h3>
      <p style={{ color: 'var(--muted)', fontSize: 12, margin: '0 0 10px' }}>
        Один аккаунт обслуживает несколько автосервисов. Данные изолированы по{' '}
        <span className="toks">tenant_id</span>.
      </p>
      {error && <p style={{ color: 'var(--red)', fontSize: 13 }}>{error}</p>}
      {tenants.map((t) => (
        <div className="rrow" key={t.id} style={{ cursor: 'default' }}>
          <div className="nm">
            <b>{t.title}</b>
            <small>
              {t.respondentCount} чел · {t.surveyCount} анкет · {t.isActive ? 'активна' : 'архив'}
            </small>
          </div>
          {t.isCurrent ? (
            <span className="chip ok">текущая</span>
          ) : (
            <button
              className="btn ghost"
              style={{ padding: '5px 10px' }}
              disabled={busy}
              onClick={() => void run(() => api.switchTenant(t.id), true)}
            >
              Переключиться
            </button>
          )}
        </div>
      ))}

      {open ? (
        <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Код (латиницей)</label>
            <input
              className="tinput"
              style={{ width: 140 }}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
            />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Название</label>
            <input className="tinput" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <button
            className="btn"
            disabled={busy || code.trim() === '' || title.trim() === ''}
            onClick={() =>
              void run(async () => {
                await api.createTenant({ code: code.trim(), title: title.trim() });
                setOpen(false);
                setCode('');
                setTitle('');
              })
            }
          >
            Создать
          </button>
          <button className="btn ghost" onClick={() => setOpen(false)}>
            Отмена
          </button>
        </div>
      ) : (
        <div className="addrow" style={{ marginTop: 12 }}>
          <button onClick={() => setOpen(true)}>+ Новая компания</button>
        </div>
      )}
    </div>
  );
}
